/**
 * Shared stock tier (BLU-711 Part B Stage 2 PR3) — Tier 4 of the expanded
 * product row. The ELECTION surface: per blank-code group, the client elects
 * which of the owner's shared SKUs backs this product's variants.
 *
 * Deliberately NOT inside VariantMatrix: elections are per blank-code GROUP
 * (the DB fan-in trigger validates whole groups at COMMIT — see
 * setVariantSharedSkuLinks' ONE-CALL-PER-GROUP contract), so a per-variant
 * cell control would invite exactly the split-group writes the backend
 * rejects. Every save submits ALL of a group's variant GIDs in one call.
 *
 * Read/write split (router access matrix): link-state chips read via
 * `getVariantSharedSkuLinks` (Coordinator+); the catalog browse + election
 * writes are Admin-only — non-admins see states but no controls, so a
 * Coordinator never hits the SkusManage 403.
 *
 * Un-election confirms name the LIVE consequence: the pre-flight
 * (`previewVariantSharedSkuLinkRemoval`, via `fetchUnelectionConsequences`)
 * says which live mappings die; if the pre-flight itself fails the dialog
 * degrades to generic consequence copy rather than blocking the removal.
 */
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Link2, Loader2 } from 'lucide-react';

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import { trpc } from '@/lib/trpc';

import { SharedSkuPicker } from './SharedSkuPicker';
import {
  chunkGids,
  groupVariantsByBaseSku,
  summarizeGroupLinks,
  type BlankCodeGroup,
  type GroupLinkSummary,
  type OwnerSharedCatalogItem,
  type SharedLinkState,
} from '../../lib/shared-stock';
import {
  fetchUnelectionConsequences,
  type UnelectionConsequences,
} from '../../lib/unelection-preflight';
import type { PreviewProduct } from '../../lib/types';

/** Mirrors the backend `setVariantSharedSkuLinks` elections cap. */
const MAX_ELECTIONS_PER_CALL = 250;

interface SharedStockTierProps {
  product: PreviewProduct;
  admin: boolean;
  /** Link-state load degraded this scan — render the honest note, not stale chips. */
  mappingStatusUnavailable: boolean;
}

function StatePill({ summary, total }: { summary: GroupLinkSummary; total: number }) {
  const { t } = useTranslation('productSync');
  let className = 'bg-muted text-muted-foreground ring-border';
  let label: string;
  if (summary.attention > 0) {
    className = 'bg-amber-100 text-amber-800 ring-amber-300/80';
    label = t('sharedStock.stateAttention');
  } else if (summary.pending > 0) {
    label = t('sharedStock.statePending');
  } else if (summary.mapped === total && total > 0) {
    className = 'bg-emerald-100 text-emerald-800 ring-emerald-300/80';
    label = t('sharedStock.stateMapped');
  } else if (summary.mapped > 0) {
    label = t('sharedStock.stateMappedPartial', { mapped: summary.mapped, count: total });
  } else {
    label = t('sharedStock.stateUnmapped');
  }
  return (
    <span
      className={cn(
        'shrink-0 whitespace-nowrap rounded-md px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ring-1',
        className,
      )}
    >
      {label}
    </span>
  );
}

interface GroupRowProps {
  group: BlankCodeGroup;
  summary: GroupLinkSummary;
  admin: boolean;
  /** Owner org elections route to; null = admin must pick one first. */
  ownerOrganizationId: string | null;
  onChanged: () => void;
}

function GroupRow({ group, summary, admin, ownerOrganizationId, onChanged }: GroupRowProps) {
  const { t } = useTranslation('productSync');
  const utils = trpc.useUtils();

  const [editing, setEditing] = useState(false);
  const [picked, setPicked] = useState<OwnerSharedCatalogItem | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [consequences, setConsequences] = useState<UnelectionConsequences | null>(null);
  const [consequencesLoading, setConsequencesLoading] = useState(false);

  const hasElection = summary.electedGids.length > 0;
  // PR #1667 review: an in-place re-election over a group with a LIVE mapping
  // never overwrites that mapping — the backend truthfully reports
  // `conflict_divergent_mapping`. So "Change" is only offered while no live
  // mapping exists; a mapped group must go Remove -> Link (the remove confirm
  // names the consequence).
  const canChangeInPlace = summary.mapped === 0;

  // Per-promotion outcome -> user-facing reason label. Reuses the attention
  // vocabulary where the semantics match; anything unrecognised folds into
  // the conflict label (mirrors the backend's own classifyBulkMapOutcome).
  const promotionIssueLabel = (status: string): string => {
    switch (status) {
      case 'shared_sku_already_claimed':
        return t('sharedStock.reason.target_claimed');
      case 'shared_sku_not_nominated':
        return t('sharedStock.reason.target_unshared');
      case 'connection_inactive':
        return t('sharedStock.reason.connection_inactive');
      default:
        return t('sharedStock.reason.mapping_conflict');
    }
  };

  const setLinks = trpc.productSync.setVariantSharedSkuLinks.useMutation({
    onSuccess: (result) => {
      // PR #1667 review: the elections upsert can succeed while promotion to
      // a live mapping did NOT happen — branch the outcome on `results[]`
      // instead of an unconditional success toast.
      const issues = result.results.filter(
        (r) => r.status !== 'created' && r.status !== 'already_mapped',
      );
      if (issues.length === 0) {
        toast.success(t('sharedStock.linkSuccess', { count: result.linkedCount }));
      } else if (issues.every((r) => r.status === 'skipped_no_client_sku')) {
        // Benign: product-sync hasn't derived the client SKU yet — the next
        // apply run promotes it on its own.
        toast.success(t('sharedStock.saveOutcomePending'));
      } else {
        const blocking = issues.find((r) => r.status !== 'skipped_no_client_sku');
        toast.error(
          t('sharedStock.saveOutcomeIssue', {
            reason: promotionIssueLabel(blocking?.status ?? ''),
          }),
        );
      }
      setEditing(false);
      setPicked(null);
      onChanged();
    },
    onError: (error) => {
      toast.error(t('sharedStock.linkError', { message: error.message }));
    },
  });

  const removeLinks = trpc.productSync.removeVariantSharedSkuLinks.useMutation({
    onSuccess: () => {
      toast.success(t('sharedStock.removeSuccess'));
      setConfirmOpen(false);
      onChanged();
    },
    onError: (error) => {
      toast.error(t('sharedStock.removeError', { message: error.message }));
    },
  });

  const handleSave = () => {
    if (!picked || !ownerOrganizationId) return;
    if (group.gids.length > MAX_ELECTIONS_PER_CALL) {
      toast.error(t('sharedStock.tooManyVariants', { max: MAX_ELECTIONS_PER_CALL }));
      return;
    }
    setLinks.mutate({
      ownerOrganizationId,
      elections: group.gids.map((gid) => ({
        shopifyVariantId: gid,
        sharedSkuId: picked.id,
        baseSkuSnapshot: group.baseSku,
      })),
    });
  };

  const openConfirm = () => {
    setConfirmOpen(true);
    setConsequences(null);
    setConsequencesLoading(true);
    void fetchUnelectionConsequences(utils, summary.electedGids).then((result) => {
      setConsequences(result);
      setConsequencesLoading(false);
    });
  };

  const doomedCodes = consequences?.doomedCodes ?? [];

  return (
    <div className="flex flex-col gap-2 rounded-md border bg-background px-3 py-2.5">
      <div className="flex flex-wrap items-center gap-2">
        <span className="font-mono text-xs font-semibold">{group.baseSku}</span>
        <span className="text-[11px] tabular-nums text-muted-foreground">
          {t('sharedStock.groupVariants', { count: group.gids.length })}
        </span>
        <StatePill summary={summary} total={group.gids.length} />
        {admin && (
          <span className="ml-auto flex items-center gap-1.5">
            {!editing && !hasElection && (
              <Button
                variant="outline"
                size="sm"
                className="h-7 gap-1.5 px-2.5 text-xs"
                disabled={!ownerOrganizationId}
                onClick={() => setEditing(true)}
              >
                <Link2 className="h-3 w-3" aria-hidden="true" />
                {t('sharedStock.link')}
              </Button>
            )}
            {!editing && hasElection && (
              <>
                {canChangeInPlace && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-7 px-2.5 text-xs"
                    disabled={!ownerOrganizationId}
                    onClick={() => setEditing(true)}
                  >
                    {t('sharedStock.change')}
                  </Button>
                )}
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-7 px-2.5 text-xs text-orange-600 hover:bg-orange-50 hover:text-orange-700"
                  onClick={openConfirm}
                >
                  {t('sharedStock.remove')}
                </Button>
              </>
            )}
          </span>
        )}
      </div>

      {summary.sharedSkuCode && !editing && (
        <span className="flex flex-wrap items-center gap-2">
          <span className="inline-flex w-fit items-center gap-1.5 rounded-md border px-2 py-0.5 text-[11px] text-muted-foreground">
            <Link2 className="h-3 w-3 shrink-0" aria-hidden="true" />
            {t('sharedStock.currentTarget', { code: summary.sharedSkuCode })}
          </span>
          {admin && !canChangeInPlace && (
            <span className="text-[11px] text-muted-foreground">
              {t('sharedStock.changeRequiresRemove')}
            </span>
          )}
        </span>
      )}

      {summary.reasons.length > 0 && (
        <ul className="space-y-0.5">
          {summary.reasons.map((reason) => (
            <li key={reason} className="flex items-start gap-1.5 text-[11px] text-amber-800">
              <span aria-hidden="true" className="mt-[3px] h-1.5 w-1.5 shrink-0 rounded-full bg-amber-500" />
              {t(`sharedStock.reason.${reason}`, { defaultValue: reason })}
            </li>
          ))}
        </ul>
      )}

      {editing && (
        <div className="flex flex-wrap items-end gap-2">
          <div className="min-w-0 flex-1">
            <SharedSkuPicker
              id={`shared-sku-picker-${group.baseSku}`}
              ownerOrganizationId={ownerOrganizationId ?? ''}
              value={picked}
              onChange={setPicked}
              disabled={!ownerOrganizationId || setLinks.isPending}
            />
          </div>
          <div className="flex items-center gap-1.5 pb-0.5">
            <Button
              size="sm"
              className="h-8 gap-1.5 bg-threadheads-purple px-3 text-xs text-white hover:bg-threadheads-purple/90"
              disabled={!picked || !ownerOrganizationId || setLinks.isPending}
              onClick={handleSave}
            >
              {setLinks.isPending && <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" />}
              {t('sharedStock.save')}
            </Button>
            <Button
              variant="ghost"
              size="sm"
              className="h-8 px-3 text-xs"
              disabled={setLinks.isPending}
              onClick={() => {
                setEditing(false);
                setPicked(null);
              }}
            >
              {t('sharedStock.cancel')}
            </Button>
          </div>
        </div>
      )}

      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('sharedStock.confirmRemoveTitle')}</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-2">
                <p>{t('sharedStock.confirmRemoveBody', { code: group.baseSku })}</p>
                {consequencesLoading ? (
                  <p className="flex items-center gap-2 text-muted-foreground">
                    <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" />
                    {t('sharedStock.confirmRemoveChecking')}
                  </p>
                ) : consequences === null ? (
                  // Pre-flight unavailable — degrade to the generic consequence.
                  <p className="text-amber-800">{t('sharedStock.confirmRemoveUnknown')}</p>
                ) : doomedCodes.length > 0 ? (
                  <p className="font-medium text-amber-800">
                    {t('sharedStock.confirmRemoveDooms', {
                      codes: doomedCodes.map((c) => c.baseSku).join(', '),
                      count: doomedCodes.length,
                    })}
                  </p>
                ) : (
                  <p>{t('sharedStock.confirmRemoveSafe')}</p>
                )}
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={removeLinks.isPending}>
              {t('sharedStock.cancel')}
            </AlertDialogCancel>
            {/* Reversible-destructive: orange outline, not destructive-red
                (DESIGN.md §13.6 — the client can re-elect at any time). */}
            <AlertDialogAction
              className="gap-1.5 border border-orange-300 bg-background text-orange-600 hover:bg-orange-50 hover:text-orange-700"
              disabled={removeLinks.isPending || consequencesLoading}
              onClick={(e) => {
                // Keep the dialog open while the mutation runs.
                e.preventDefault();
                removeLinks.mutate({ shopifyVariantIds: summary.electedGids });
              }}
            >
              {removeLinks.isPending && <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" />}
              {t('sharedStock.confirmRemoveAction')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

export function SharedStockTier({ product, admin, mappingStatusUnavailable }: SharedStockTierProps) {
  const { t } = useTranslation('productSync');
  const utils = trpc.useUtils();

  const { groups, underivedCount } = useMemo(() => groupVariantsByBaseSku(product), [product]);
  const allGids = useMemo(() => groups.flatMap((g) => g.gids), [groups]);
  // PR #1667 review blocker: the link-state read is a GET behind the 2083-char
  // URL cap — chunk to LINK_STATE_GID_CHUNK per query and merge in chunk
  // order (stable: chunks are slices of `allGids`' own order).
  const gidChunks = useMemo(() => chunkGids(allGids), [allGids]);
  const linkQueries = trpc.useQueries((tq) =>
    gidChunks.map((chunk) =>
      tq.productSync.getVariantSharedSkuLinks(
        { variantGids: chunk },
        { enabled: !mappingStatusUnavailable, staleTime: 15_000 },
      ),
    ),
  );
  const linksLoading = linkQueries.some((q) => q.isLoading);
  const linksError = linkQueries.some((q) => q.isError);

  // Catalog browse + election writes are SkusManage/Admin — never issue the
  // owners read for a Coordinator (they'd 403).
  const ownersQuery = trpc.inventory.listSharedStockOwners.useQuery(undefined, {
    enabled: admin,
    staleTime: 300_000,
  });
  const owners = ownersQuery.data?.owners ?? [];

  // Not memoised: linkQueries' identity churns per render anyway, and the
  // merge is O(variants). Chunk order keeps the merge stable.
  const stateByGid = new Map<string, SharedLinkState>();
  for (const query of linkQueries) {
    for (const state of query.data?.states ?? []) stateByGid.set(state.shopifyVariantId, state);
  }

  const summaries = groups.map((group) => summarizeGroupLinks(group, stateByGid));

  // Owner the elections route to: an explicit pick wins; else the owner the
  // existing elections already point at; else the only connected owner.
  const [ownerChoice, setOwnerChoice] = useState<string | null>(null);
  const electedOwnerId = summaries.find((s) => s.ownerOrganizationId)?.ownerOrganizationId ?? null;
  const effectiveOwnerId =
    ownerChoice ?? electedOwnerId ?? (owners.length === 1 ? (owners[0]?.id ?? null) : null);

  const handleChanged = () => {
    void utils.productSync.getVariantSharedSkuLinks.invalidate();
    void utils.productSync.getProductSyncPreview.invalidate();
  };

  if (mappingStatusUnavailable) {
    return <p className="text-xs text-amber-700">{t('sharedStock.unavailable')}</p>;
  }

  if (allGids.length === 0) {
    return <p className="text-xs text-muted-foreground">{t('sharedStock.noDerived')}</p>;
  }

  if (linksLoading) {
    return (
      <div className="space-y-2" aria-hidden="true">
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-10 w-full" />
      </div>
    );
  }

  if (linksError) {
    return (
      <div className="flex flex-wrap items-center gap-2">
        <p className="text-xs text-destructive">{t('sharedStock.statesError')}</p>
        <Button
          variant="outline"
          size="sm"
          className="h-7 px-2.5 text-xs"
          onClick={() => {
            for (const query of linkQueries) {
              if (query.isError) void query.refetch();
            }
          }}
        >
          {t('sharedStock.statesRetry')}
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {admin && owners.length === 0 && !ownersQuery.isLoading && (
        <p className="text-xs text-muted-foreground">{t('sharedStock.noOwners')}</p>
      )}
      {admin && owners.length > 1 && (
        <div className="flex max-w-sm flex-col gap-1.5">
          <Label htmlFor={`shared-stock-owner-${product.id}`} className="text-sm">
            {t('sharedStock.ownerLabel')}
          </Label>
          <Select
            value={effectiveOwnerId ?? undefined}
            onValueChange={(v) => setOwnerChoice(v)}
          >
            <SelectTrigger id={`shared-stock-owner-${product.id}`} className="h-8 text-xs">
              <SelectValue placeholder={t('sharedStock.ownerPlaceholder')} />
            </SelectTrigger>
            <SelectContent>
              {owners.map((owner) => (
                <SelectItem key={owner.id} value={owner.id}>
                  <span className="flex items-center gap-2">
                    <Link2 className="h-3.5 w-3.5 shrink-0 text-threadheads-purple" aria-hidden="true" />
                    {owner.name}
                  </span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}

      <div className="space-y-2">
        {groups.map((group, i) => (
          <GroupRow
            key={group.baseSku}
            group={group}
            summary={summaries[i]!}
            admin={admin}
            ownerOrganizationId={effectiveOwnerId}
            onChanged={handleChanged}
          />
        ))}
      </div>

      {underivedCount > 0 && (
        <p className="text-[11px] text-muted-foreground">
          {t('sharedStock.underived', { count: underivedCount })}
        </p>
      )}
    </div>
  );
}
